import { useState } from 'react'

import clsx from 'clsx'
import _ from 'lodash'

import { PageMeta } from '@/shared/lib/meta/page-meta'
import { PageBackButton } from '@/shared/ui/Layout/page/PageBackButton'

import { boxedPartnerCategories } from '../lib/partnerCategories'


/**
 * Premium profile cover (handoff bloco 1). Short 132px cover image (full-bleed
 * via `-mx-3` inside `Screen.Content`) and — when the partner has a category
 * — a sponsorship seal pill. Back button overlays the cover; page title lives
 * in `PartnerIdentityCard`, which overlaps the cover bottom in the same scroll
 * area (`-mt-8`).
 *
 * Named "Cover" (not "Banner") on purpose: a file path containing "banner" is
 * blocked by ad blockers (ERR_BLOCKED_BY_CLIENT), which broke the whole route.
 *
 * The gold accent (#FBBF24) and the dark translucent pill are brand-tier
 * decoration, intentionally outside the token system (same rationale as the
 * WhatsApp color): a sponsorship seal is not a semantic UI state.
 */
/**
 * Três CAIXAS cabem nos 116px úteis da capa — e o contador é uma delas.
 *
 * 3 × ~26px + 2 × 4px de gap = 86px, dentro dos 116px que sobram sob o
 * `top-4`. Uma quarta caixa levaria o empilhamento a 116px cravados, e a
 * última cairia nos 32px que o `PartnerIdentityCard` cobre com `-mt-8`.
 */
const COVER_CATEGORY_BOXES = 3

export const PartnerCover = ({
  partner,
  coverUrl: coverUrlProp,
  backHref = '/parceiros',
  className = ''
}) => {
  // Prefer the explicit URL (premium profile banner) and fall back to the record.
  const coverUrl = coverUrlProp || _.get(partner, 'banner.file.url')
  // O orçamento é de caixas, e o contador é uma caixa: com excedente saem
  // duas pílulas e o contador, nunca três pílulas e o contador.
  const { names: categoryNames, visible: visibleCategories, overflowCount } =
    boxedPartnerCategories(partner?.partner_categories, COVER_CATEGORY_BOXES)
  const pageTitle = partner?.name ?? 'Parceiro'
  const [failed, setFailed] = useState(false)

  return (
    <>
      <PageMeta title={pageTitle} />
      <div className={clsx('relative -mx-3', className)}>
        {coverUrl && !failed ? (
          <img
            src={coverUrl}
            alt={partner?.name}
            onError={() => setFailed(true)}
            className="h-[132px] w-full object-cover object-center"
          />
        ) : (
          <div className="h-[132px] w-full bg-brand-light" />
        )}

        <div className="absolute left-3 top-3 z-10">
          <PageBackButton variant="surface" backHref={backHref} />
        </div>

        {categoryNames.length > 0 && (
          <>
            {/* `aria-label` num `<div>` sem `role` não é anunciado — ele mapeia
                para `role=generic`, e o leitor de tela acabava lendo as pílulas
                e um "+N" nu, perdendo justamente as categorias que o contador
                resume. Mesmo padrão do card: pilha visual fora da árvore de
                acessibilidade, lista inteira num texto anunciável ao lado. */}
            <div
              aria-hidden
              data-testid="partner-cover-categories"
              className="absolute right-3 top-4 z-10 flex max-w-[60%] flex-col items-end gap-1"
            >
              {visibleCategories.map(categoryName => (
                <div
                  key={categoryName}
                  className="flex max-w-full items-center gap-1.5 rounded-full bg-[rgba(30,27,46,.72)] px-2.5 py-1 backdrop-blur-sm"
                >
                  <span
                    className="h-2 w-2 shrink-0 rounded-[2px] bg-[#FBBF24]"
                    aria-hidden
                  />
                  <span className="truncate text-2xs font-bold uppercase tracking-[.04em] text-white">
                    {categoryName}
                  </span>
                </div>
              ))}

              {overflowCount > 0 && (
                <div className="rounded-full bg-[rgba(30,27,46,.72)] px-2.5 py-1 text-2xs font-bold uppercase tracking-[.04em] text-white backdrop-blur-sm">
                  +{overflowCount}
                </div>
              )}
            </div>

            <span className="sr-only">
              {`Categorias: ${categoryNames.join(', ')}`}
            </span>
          </>
        )}
      </div>
    </>
  )
}
